import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';

// Import a consultation icon (using a video call emoji as placeholder, you can replace with an actual image)
// import digitaleSprechstundeIcon from '../assets/digitale_sprechstunde_icon.png';

// Styled components
const DigitaleSprechstundeIconContainer = styled(Box)(({ theme, showTooltip }) => ({
  position: 'fixed',
  bottom: '260px',
  right: '20px',
  zIndex: showTooltip ? 9990 : 9998, // Lower z-index when tooltip is shown
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}));

const DigitaleSprechstundeIconOverlay = styled(Box)(({ theme }) => ({
  width: '60px',
  height: '60px',
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  transition: 'all 0.3s ease-in-out',
  boxShadow: '0 4px 20px rgba(156, 39, 176, 0.3)',
  border: '2px solid rgba(255, 255, 255, 0.2)',
  '&:hover': {
    transform: 'scale(1.05)',
    boxShadow: '0 6px 25px rgba(156, 39, 176, 0.5)',
  },
}));

const DigitaleSprechstundeTooltip = styled(Box)(({ theme }) => ({
  position: 'absolute',
  bottom: '10px',
  right: '80px',
  width: '280px',
  background: 'linear-gradient(135deg, #7b1fa2 0%, #6a1b9a 100%)',
  border: '2px solid #9c27b0',
  borderRadius: '12px',
  padding: theme.spacing(2),
  zIndex: 10011,
  boxShadow: '0 12px 40px rgba(156, 39, 176, 0.4)',
  backdropFilter: 'blur(10px)',
  animation: 'tooltipSlideIn 0.3s ease-out',
  '&::before': {
    content: '""',
    position: 'absolute',
    bottom: '20px',
    right: '-8px',
    width: 0,
    height: 0,
    borderTop: '8px solid transparent',
    borderBottom: '8px solid transparent',
    borderLeft: '8px solid #7b1fa2', // Purple arrow pointing right
  },
  '@keyframes tooltipSlideIn': {
    '0%': {
      opacity: 0,
      transform: 'translateY(10px) scale(0.95)',
    },
    '100%': {
      opacity: 1,
      transform: 'translateY(0) scale(1)',
    },
  },
}));

const DigitaleSprechstundeIcon = () => {
  const [showTooltip, setShowTooltip] = useState(false);
  const [showHoverTooltip, setShowHoverTooltip] = useState(false);

  const handleIconClick = () => {
    setShowTooltip(!showTooltip);
  };

  const handleBookNow = () => {
    window.open('https://cyberlagebild.de/termine', '_blank');
    setShowTooltip(false);
  };

  const handleLater = () => {
    setShowTooltip(false);
  };

  return (
    <DigitaleSprechstundeIconContainer showTooltip={showTooltip || showHoverTooltip}>
      {/* Digitale Sprechstunde Icon */}
      <DigitaleSprechstundeIconOverlay
        onClick={handleIconClick}
        onMouseEnter={() => setShowHoverTooltip(true)}
        onMouseLeave={() => setShowHoverTooltip(false)}
        title="Digitale Sprechstunde"
      >
        {/* Using calendar with clock emoji for consultation/office hours */}
        <Typography
          sx={{
            fontSize: '28px',
            color: '#ffffff',
            textShadow: '0 2px 4px rgba(0, 0, 0, 0.5)',
          }}
        >
          📅
        </Typography>
        {/* Uncomment when you have an actual image:
        <img
          src={digitaleSprechstundeIcon}
          alt="Digitale Sprechstunde"
          style={{
            width: '60px',
            height: '60px',
            objectFit: 'contain',
          }}
        />
        */}
      </DigitaleSprechstundeIconOverlay>
      
      {showHoverTooltip && (
        <DigitaleSprechstundeTooltip>
          <Typography variant="body2" sx={{ color: 'white', fontWeight: 'bold', mb: 1 }}>
            Digitale Sprechstunde
          </Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.9)' }}>
            Beratung zum Thema Cybersicherheit
          </Typography>
        </DigitaleSprechstundeTooltip>
      )}

      {/* Digitale Sprechstunde Tooltip */}
      {showTooltip && (
        <DigitaleSprechstundeTooltip>
          <Typography
            variant="h6"
            sx={{
              color: '#ffffff',
              fontWeight: 'bold',
              marginBottom: 1,
              textAlign: 'center',
            }}
          >
            Digitale Sprechstunde – Aus Fragen wird Sicherheit.
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: '#f3e5f5',
              marginBottom: 2,
              textAlign: 'center',
              fontStyle: 'italic',
            }}
          >
            Deine persönliche Beratung zum Thema Cybersicherheit
          </Typography>
          
          <Typography
            variant="body2"
            sx={{
              color: '#e1bee7',
              marginBottom: 2,
              textAlign: 'center',
              fontSize: '0.85rem',
            }}
          >
            Sprich mit unseren Expert:innen über deine Anliegen.
          </Typography>
          
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="small"
              onClick={handleBookNow}
              sx={{
                background: 'linear-gradient(135deg, #ba68c8 0%, #9c27b0 100%)',
                color: '#ffffff',
                fontWeight: 'bold',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #9c27b0 0%, #7b1fa2 100%)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              Termine anzeigen
            </Button>
            
            <Button
              variant="outlined"
              size="small"
              onClick={handleLater}
              sx={{
                color: '#ffffff',
                borderColor: '#ffffff',
                padding: '6px 16px',
                borderRadius: '6px',
                fontSize: '0.8rem',
                '&:hover': {
                  backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  borderColor: '#f3e5f5',
                },
              }}
            >
              Später
            </Button>
          </Box>
        </DigitaleSprechstundeTooltip>
      )}
    </DigitaleSprechstundeIconContainer>
  );
};

export default DigitaleSprechstundeIcon;
